import React, { useState, useEffect } from 'react';
import { generateItemName } from '../../services/inventoryService';
import { ItemStatus, ItemType, SilverItem, WatchItem, Currency, InventoryItem } from '../../types/item';
import { useCategory } from '../../context/CategoryContext';
import { useInventory } from '../../context/InventoryContext';
import { FormModal, FormField, TextInput, SelectInput, NumberInput } from '../ui';
import { useResponsive, useBarcodeRegistration } from '../../hooks';
import { BarcodeInputTargetType } from '../../services/barcodeService';
import { calculateCostPerUnit, calculateSellPerUnit } from '../../utils/priceUtils';
import { formatCurrency } from '../../services/exchangeRateService';
// import { generateEAN8Barcode, validateEAN8 } from '../../utils/barcodeUtils'; // TODO: Replace with Data Matrix imports
// TODO: Import generateDataMatrixBarcode, validateDataMatrix when available
import { generateDataMatrixBarcode, validateDataMatrix } from '../../utils/barcodeUtils'; // Import new functions
import { checkBarcodeExists } from '../../services/inventoryService';

// LocalStorage keys for form preferences
const STORAGE_KEY_PREFIX = 'inventory_form_';
const STORAGE_KEYS = {
  ITEM_TYPE: `${STORAGE_KEY_PREFIX}item_type`,
  CATEGORY: `${STORAGE_KEY_PREFIX}category`,
  COST_CURRENCY: `${STORAGE_KEY_PREFIX}cost_currency`,
  SELL_CURRENCY: `${STORAGE_KEY_PREFIX}sell_currency`,
  PRICING_MODE: `${STORAGE_KEY_PREFIX}pricing_mode`,
  PURCHASE_DATE: `${STORAGE_KEY_PREFIX}purchase_date`,
};

interface AddItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialBarcode?: string;
}

// Form context ID
const ADD_ITEM_FORM_CONTEXT_ID = 'add-item-form';

/**
 * Load preferences from localStorage
 */
const loadPreferences = () => {
  const preferences = {
    itemType: localStorage.getItem(STORAGE_KEYS.ITEM_TYPE) || String(ItemType.SILVER),
    category: localStorage.getItem(STORAGE_KEYS.CATEGORY) || '',
    costCurrency: localStorage.getItem(STORAGE_KEYS.COST_CURRENCY) || Currency.USD,
    sellCurrency: localStorage.getItem(STORAGE_KEYS.SELL_CURRENCY) || Currency.USD,
    pricingMode: localStorage.getItem(STORAGE_KEYS.PRICING_MODE) || 'perGram',
    purchaseDate: localStorage.getItem(STORAGE_KEYS.PURCHASE_DATE) || new Date().toISOString().split('T')[0],
  };
  
  return preferences;
};

/**
 * Save preferences to localStorage
 */
const savePreferences = (preferences: {
  itemType: string;
  category: string;
  costCurrency: string;
  sellCurrency: string;
  pricingMode: string;
  purchaseDate: string;
}) => {
  localStorage.setItem(STORAGE_KEYS.ITEM_TYPE, preferences.itemType);
  localStorage.setItem(STORAGE_KEYS.CATEGORY, preferences.category);
  localStorage.setItem(STORAGE_KEYS.COST_CURRENCY, preferences.costCurrency);
  localStorage.setItem(STORAGE_KEYS.SELL_CURRENCY, preferences.sellCurrency);
  localStorage.setItem(STORAGE_KEYS.PRICING_MODE, preferences.pricingMode);
  localStorage.setItem(STORAGE_KEYS.PURCHASE_DATE, preferences.purchaseDate);
};

/**
 * Consolidated responsive AddItemForm component 
 * Handles both mobile and desktop views using FormModal's responsive capabilities
 */
const AddItemForm: React.FC<AddItemFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialBarcode = '',
}) => {
  useResponsive();
  
  // Load saved preferences when form is opened
  useEffect(() => {
    if (isOpen) {
      const prefs = loadPreferences();
      
      // Only set these values if the form is freshly opened (not when it's already populated)
      if (isOpen && !barcode) {
        setItemType(prefs.itemType as ItemType);
        setCategory(prefs.category);
        setCostCurrency(prefs.costCurrency as Currency);
        setSellCurrency(prefs.sellCurrency as Currency);
        setPricingMode(prefs.pricingMode as 'perGram' | 'perUnit');
        setPurchaseDate(new Date(prefs.purchaseDate));
      }
    }
  }, [isOpen]);
  
  // Add state to track pricing mode
  const [pricingMode, setPricingMode] = useState<'perUnit' | 'perGram'>('perGram');
  
  const [itemType, setItemType] = useState<ItemType>(ItemType.SILVER);
  const [name, setName] = useState('');
  const [barcode, setBarcode] = useState(initialBarcode);
  const [category, setCategory] = useState('');
  // Common pricing fields
  const [costPerUnit, setCostPerUnit] = useState(0);
  const [sellPerUnit, setSellPerUnit] = useState(0);
  // Currency fields
  const [costCurrency, setCostCurrency] = useState<Currency>(Currency.USD);
  const [sellCurrency, setSellCurrency] = useState<Currency>(Currency.USD);
  const [purchaseDate, setPurchaseDate] = useState<Date>(new Date());
  // Silver-specific fields
  const [weight, setWeight] = useState<number>(0);
  const [pricePerGram, setPricePerGram] = useState<number>(0);
  const [costPerGram, setCostPerGram] = useState<number>(0);
  // Watch-specific fields
  const [brand, setBrand] = useState<string>('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track if barcode was generated by the system
  const [isBarcodeGenerated, setIsBarcodeGenerated] = useState<boolean>(false);
  
  // Get data from contexts
  const { categories, loading: categoriesLoading } = useCategory();
  const { createSilverItem, createWatchItem, fetchItems, items, findByBarcode } = useInventory();
  
  // Get responsive breakpoints
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  
  // Register with barcode service using the hook
  useBarcodeRegistration({
    formId: ADD_ITEM_FORM_CONTEXT_ID,
    formFields: [{ id: 'barcode', type: BarcodeInputTargetType.FORM_FIELD }],
    isActive: isOpen
  });
  
  // Set initial category when categories changes
  useEffect(() => {
    const filteredCategories = categories.filter(category => category.typeId === itemType);
    if (filteredCategories.length > 0 && !category) {
      setCategory(filteredCategories[0].id);
    }
  }, [categories, category, itemType]);
  
  // Calculate derived values based on inputs
  useEffect(() => {
    if (itemType === ItemType.SILVER) {
      if (pricingMode === 'perGram' && weight > 0) {
        // Use utility functions for calculations
        const silverData = { weight, pricePerGram, costPerGram };
        const calculatedSellPerUnit = calculateSellPerUnit(silverData);
        const calculatedCostPerUnit = calculateCostPerUnit(silverData);
        
        setSellPerUnit(calculatedSellPerUnit);
        setCostPerUnit(calculatedCostPerUnit);
      } else if (pricingMode === 'perUnit' && weight > 0) {
        // Calculate pricePerGram from sellPerUnit / weight
        const calculatedPricePerGram = Number((sellPerUnit / weight).toFixed(2));
        
        // Calculate costPerGram from costPerUnit / weight
        const calculatedCostPerGram = costPerUnit > 0 ? Number((costPerUnit / weight).toFixed(2)) : 0;
        
        setPricePerGram(calculatedPricePerGram);
        setCostPerGram(calculatedCostPerGram);
      }
    }
  }, [itemType, weight, pricePerGram, costPerGram, sellPerUnit, costPerUnit, pricingMode]);
  
  const handleTypeChange = (newType: string) => {
    // Convert string to ItemType enum
    const typeValue = newType as ItemType;
    setItemType(typeValue);
    
    // Reset pricing values
    setSellPerUnit(0);
    setCostPerUnit(0);
    
    // Reset category to ensure we don't keep a category from a different type
    setCategory('');
    
    if (typeValue === ItemType.SILVER) {
      setWeight(0);
      setPricePerGram(0);
      setCostPerGram(0);
      setBrand('');
      setPricingMode('perGram');
    } else {
      setWeight(0);
      setPricePerGram(0);
      setCostPerGram(0);
      setBrand('');
      setPricingMode('perUnit');
    }
    
    // Reset barcode when changing item type
    if (isBarcodeGenerated) {
      setBarcode('');
      setIsBarcodeGenerated(false);
    }
  };
  
  // Generate a unique barcode
  const handleGenerateBarcode = async () => {
    setError(null);
    setLoading(true);
    
    try {
      // Use the existing items array instead of fetching all items again
      // const existingBarcodes = items.map((item: { barcode: string }) => item.barcode); // Remove this
      
      // Generate a new EAN-8 barcode
      // const newBarcode = generateEAN8Barcode(itemType, existingBarcodes); // TODO: Replace with Data Matrix generation
      // const newBarcode = `DM-${Date.now()}-${Math.random().toString(16).substring(2, 8)}`; // Temporary placeholder generation
      // const newBarcode = generateDataMatrixBarcode(itemType, existingBarcodes); // Use Data Matrix generation
      const newBarcode = await generateDataMatrixBarcode(itemType); // Await the new async function
      
      // Set the barcode
      setBarcode(newBarcode);
      setIsBarcodeGenerated(true);
      setLoading(false);
    } catch (error) {
      console.error('Error generating barcode:', error);
      setError('Barkod oluşturulurken bir hata oluştu.');
      setLoading(false);
    }
  };
  
  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow only digits
    if (value === '' || /^\d*$/.test(value)) {
      setBarcode(value);
      
      // If user manually entered barcode, it's no longer considered "generated"
      if (isBarcodeGenerated && value !== barcode) {
        setIsBarcodeGenerated(false);
      }
      
      // Clear any existing error if barcode is empty or being modified
      if (error && error.includes('barkod')) {
        setError(null);
      }
    }
  };
  
  // Validate EAN-8 barcode
  const validateBarcode = (): boolean => {
    // Check if empty
    if (!barcode) {
      setError('Barkod girilmelidir.');
      return false;
    }
    
    // Validate EAN-8 format
    // if (!validateEAN8(barcode)) { // TODO: Replace with Data Matrix validation
    //   setError('Geçersiz barkod formatı. Yalnızca geçerli EAN-8 barkodları kullanılabilir.');
    //   return false;
    // }
    // TODO: Implement Data Matrix validation if needed (e.g., check prefix/length)
    if (!validateDataMatrix(barcode)) { // Use Data Matrix validation
      setError('Geçersiz barkod formatı. Beklenen format: DM-<Tip>-<Zaman Damgası>-<Rastgele>');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;

    setLoading(true);
    setError(null);
    
    // Validate required fields
    if (!category) {
      setError('Lütfen bir kategori seçin.');
      setLoading(false);
      return;
    }
    
    // Validate barcode format
    if (!validateBarcode()) {
      setLoading(false);
      return;
    }
    
    try {
      // Check if barcode already exists in the database for manually entered barcodes
      if (!isBarcodeGenerated) {
        const barcodeExists = await checkBarcodeExists(barcode);
        if (barcodeExists) {
          setError('Bu barkod zaten kullanılıyor. Lütfen başka bir barkod girin veya otomatik oluşturun.');
          setLoading(false);
          return;
        }
      }
      
      // Validate item type specific fields
      if (itemType === ItemType.SILVER) {
        if (weight <= 0) {
          setError('Ağırlık sıfırdan büyük olmalıdır.');
          setLoading(false);
          return;
        }
        
        if (pricePerGram <= 0) {
          setError('Gram başına fiyat sıfırdan büyük olmalıdır.');
          setLoading(false);
          return;
        }
      } else {
        if (sellPerUnit <= 0) {
          setError('Satış fiyatı sıfırdan büyük olmalıdır.');
          setLoading(false);
          return;
        }
      }
    
      // Generate name if not provided
      const itemName = name || await generateItemName(itemType, category);
      
      // Save preferences before creating the item
      savePreferences({
        itemType: String(itemType),
        category,
        costCurrency,
        sellCurrency,
        pricingMode,
        purchaseDate: formatDateForInput(purchaseDate)
      });
      
      if (itemType === ItemType.SILVER) {
        // Create a silver item
        const silverItem: Omit<SilverItem, 'id' | 'createdAt' | 'updatedAt'> = {
          type: ItemType.SILVER,
          name: itemName,
          barcode,
          category,
          weight,
          pricePerGram,
          costPerGram,
          costPerUnit,
          sellPerUnit,
          costCurrency,
          sellCurrency,
          purchaseDate,
          userId: '', // Will be set by the service
          status: ItemStatus.AVAILABLE
        };
        
        await createSilverItem(silverItem);
      } else {
        // Create a watch item
        const watchItem: Omit<WatchItem, 'id' | 'createdAt' | 'updatedAt'> = {
          type: ItemType.WATCH,
          name: itemName,
          barcode,
          category,
          brand,
          costPerUnit,
          sellPerUnit,
          costCurrency,
          sellCurrency,
          purchaseDate,
          userId: '', // Will be set by the service
          status: ItemStatus.AVAILABLE
        };
        
        await createWatchItem(watchItem);
      }
      
      // Clear form state - but keep preferences for next item
      setName('');
      setBarcode('');
      setCostPerUnit(0);
      setSellPerUnit(0);
      setWeight(0);
      setPricePerGram(0);
      setCostPerGram(0);
      setBrand('');
      setIsBarcodeGenerated(false);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the add item form
      onClose();
    } catch (error) {
      console.error('Error creating item:', error);
      setError('Ürün eklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };
  
  // Get the appropriate categories for the selected item type
  const typeCategories = categories.filter(cat => cat.typeId === itemType);

  // Handle NumberInput changes
  const handleNumberInputChange = (setter: React.Dispatch<React.SetStateAction<number>>) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      // Allow empty string or valid numeric input (including decimals in progress)
      if (inputValue === '' || inputValue === '.') {
        setter(0);
      } else if (inputValue.match(/^-?\d*\.?\d*$/)) {
        // Only parse to number when we have a valid numeric value
        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
          setter(numericValue);
        }
      }
    };
  };

  // Currency options for select inputs
  const currencyOptions = [
    { value: Currency.TRY, label: 'TRY (₺)' },
    { value: Currency.USD, label: 'USD ($)' }
  ];

  // Format price with currency
  const formatPriceWithCurrency = (price: number, currency: Currency): string => {
    return formatCurrency(price, currency);
  };

  // Format date to YYYY-MM-DD for input
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value ? new Date(e.target.value) : new Date();
    setPurchaseDate(newDate);
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Yeni Ürün Ekle"
      size={isMobile ? "full" : "large"}
      onSubmit={handleSubmit}
      isSubmitting={loading}
    >
      {error && (
        <div className="mb-4 p-2 bg-red-100 border border-red-300 text-red-700 rounded">
          {error}
        </div>
      )}
      
      <div className="space-y-4 w-full">
        <FormField id="item-type-field" label="Ürün Tipi" required={true}>
          <SelectInput
            id="item-type"
            name="itemType"
            value={String(itemType)}
            onChange={(e) => handleTypeChange(e.target.value)}
            options={[
              { value: String(ItemType.SILVER), label: 'Gümüş' },
              { value: String(ItemType.WATCH), label: 'Saat' }
            ]}
            disabled={loading}
            className="w-full"
          />
        </FormField>
        
        <FormField id="barcode-field" label="Barkod" required={true}>
          <div className="relative w-full">
            <TextInput
              id="barcode"
              name="barcode"
              value={barcode}
              onChange={handleBarcodeChange}
              placeholder="Barkod"
              disabled={loading}
              maxLength={8}
              className="w-full pr-[100px]"
            />
            <button 
              type="button"
              onClick={handleGenerateBarcode}
              className="absolute right-0 top-0 bottom-0 w-[100px] bg-primary-charcoal text-accent-white text-sm border border-primary-charcoal rounded-r hover:bg-primary-charcoal/90 disabled:bg-primary-charcoal/50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Oluştur
            </button>
          </div>
        </FormField>
        
        <FormField id="category-field" label="Kategori" required={true}>
          <SelectInput
            id="category"
            name="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={typeCategories.map(cat => ({
              value: cat.id,
              label: cat.name
            }))}
            disabled={loading || categoriesLoading}
            placeholder={categoriesLoading ? 'Yükleniyor...' : 'Kategori seçin'}
            className="w-full"
          />
        </FormField>
        
        <FormField id="name-field" label="Ürün Adı" required={false}>
          <TextInput
            id="name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ürün adı (boş bırakılırsa otomatik oluşturulur)"
            disabled={loading}
            className="w-full"
          />
        </FormField>

        <FormField id="purchase-date-field" label="Satın Alma Tarihi" required={false}>
          <input
            type="date"
            id="purchase-date"
            name="purchaseDate"
            value={formatDateForInput(purchaseDate)}
            onChange={handleDateChange}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-charcoal/30 focus:border-primary-charcoal text-sm md:text-base"
          />
        </FormField>
        
        {itemType === ItemType.SILVER && (
          <>
            <div className="flex justify-end mb-2">
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                    pricingMode === 'perGram'
                      ? 'bg-primary-charcoal text-accent-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border border-gray-300`}
                  onClick={() => setPricingMode('perGram')}
                >
                  Gram Fiyatı
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                    pricingMode === 'perUnit'
                      ? 'bg-primary-charcoal text-accent-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border border-gray-300 border-l-0`}
                  onClick={() => setPricingMode('perUnit')}
                >
                  Birim Fiyatı
                </button>
              </div>
            </div>
            
            <FormField id="weight-field" label="Ağırlık (gr)" required={true}>
              <NumberInput
                id="weight"
                name="weight"
                value={weight}
                onChange={handleNumberInputChange(setWeight)}
                min={0}
                step={0.01}
                precision={2}
                disabled={loading}
              />
            </FormField>
            
            {pricingMode === 'perGram' ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField id="price-per-gram-field" label="Satış Fiyatı (gr)" required={true}>
                    <NumberInput
                      id="price-per-gram"
                      name="pricePerGram"
                      value={pricePerGram}
                      onChange={handleNumberInputChange(setPricePerGram)}
                      min={0}
                      step={0.01}
                      precision={2}
                      disabled={loading}
                    />
                  </FormField>
                  
                  <FormField id="sell-currency-field" label="Para Birimi" required={true}>
                    <SelectInput
                      id="sell-currency"
                      name="sellCurrency"
                      value={sellCurrency}
                      onChange={(e) => setSellCurrency(e.target.value as Currency)}
                      options={currencyOptions}
                      disabled={loading}
                    />
                  </FormField>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField id="cost-per-gram-field" label="Maliyet Fiyatı (gr)" required={false}>
                    <NumberInput
                      id="cost-per-gram"
                      name="costPerGram"
                      value={costPerGram}
                      onChange={handleNumberInputChange(setCostPerGram)}
                      min={0}
                      step={0.01}
                      precision={2}
                      disabled={loading}
                    />
                  </FormField>
                  
                  <FormField id="cost-currency-field" label="Para Birimi" required={true}>
                    <SelectInput
                      id="cost-currency"
                      name="costCurrency"
                      value={costCurrency}
                      onChange={(e) => setCostCurrency(e.target.value as Currency)}
                      options={currencyOptions}
                      disabled={loading}
                    />
                  </FormField>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 p-2 bg-gray-50 rounded-md">
                  <div className="text-sm">
                    <div className="font-semibold text-gray-600">Toplam Satış Fiyatı:</div>
                    <div className="text-lg font-bold text-primary-charcoal">
                      {formatPriceWithCurrency(sellPerUnit, sellCurrency)}
                    </div>
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-gray-600">Toplam Maliyet:</div>
                    <div className="text-lg font-bold text-primary-charcoal">
                      {formatPriceWithCurrency(costPerUnit, costCurrency)}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField id="sell-per-unit-field" label="Satış Fiyatı" required={true}>
                    <NumberInput
                      id="sell-per-unit"
                      name="sellPerUnit"
                      value={sellPerUnit}
                      onChange={handleNumberInputChange(setSellPerUnit)}
                      min={0}
                      step={0.01}
                      precision={2}
                      disabled={loading}
                    />
                  </FormField>
                  
                  <FormField id="sell-currency-field" label="Para Birimi" required={true}>
                    <SelectInput
                      id="sell-currency"
                      name="sellCurrency"
                      value={sellCurrency}
                      onChange={(e) => setSellCurrency(e.target.value as Currency)}
                      options={currencyOptions}
                      disabled={loading}
                    />
                  </FormField>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField id="cost-per-unit-field" label="Maliyet Fiyatı" required={false}>
                    <NumberInput
                      id="cost-per-unit"
                      name="costPerUnit"
                      value={costPerUnit}
                      onChange={handleNumberInputChange(setCostPerUnit)}
                      min={0}
                      step={0.01}
                      precision={2}
                      disabled={loading}
                    />
                  </FormField>
                  
                  <FormField id="cost-currency-field" label="Para Birimi" required={true}>
                    <SelectInput
                      id="cost-currency"
                      name="costCurrency"
                      value={costCurrency}
                      onChange={(e) => setCostCurrency(e.target.value as Currency)}
                      options={currencyOptions}
                      disabled={loading}
                    />
                  </FormField>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 p-2 bg-gray-50 rounded-md">
                  <div className="text-sm">
                    <div className="font-semibold text-gray-600">Toplam Satış Fiyatı:</div>
                    <div className="text-lg font-bold text-primary-charcoal">
                      {formatPriceWithCurrency(sellPerUnit, sellCurrency)}
                    </div>
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-gray-600">Toplam Maliyet:</div>
                    <div className="text-lg font-bold text-primary-charcoal">
                      {formatPriceWithCurrency(costPerUnit, costCurrency)}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        
        {itemType === ItemType.WATCH && (
          <>
            <FormField id="brand-field" label="Marka" required={false}>
              <TextInput
                id="brand"
                name="brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Marka"
                disabled={loading}
              />
            </FormField>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField id="sell-per-unit-field" label="Satış Fiyatı" required={true}>
                <NumberInput
                  id="sell-per-unit"
                  name="sellPerUnit"
                  value={sellPerUnit}
                  onChange={handleNumberInputChange(setSellPerUnit)}
                  min={0}
                  step={0.01}
                  precision={2}
                  disabled={loading}
                />
              </FormField>
              
              <FormField id="sell-currency-field" label="Para Birimi" required={true}>
                <SelectInput
                  id="sell-currency"
                  name="sellCurrency"
                  value={sellCurrency}
                  onChange={(e) => setSellCurrency(e.target.value as Currency)}
                  options={currencyOptions}
                  disabled={loading}
                />
              </FormField>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField id="cost-per-unit-field" label="Maliyet Fiyatı" required={false}>
                <NumberInput
                  id="cost-per-unit"
                  name="costPerUnit"
                  value={costPerUnit}
                  onChange={handleNumberInputChange(setCostPerUnit)}
                  min={0}
                  step={0.01}
                  precision={2}
                  disabled={loading}
                />
              </FormField>
              
              <FormField id="cost-currency-field" label="Para Birimi" required={true}>
                <SelectInput
                  id="cost-currency"
                  name="costCurrency"
                  value={costCurrency}
                  onChange={(e) => setCostCurrency(e.target.value as Currency)}
                  options={currencyOptions}
                  disabled={loading}
                />
              </FormField>
            </div>
          </>
        )}
      </div>
    </FormModal>
  );
};

export default AddItemForm; 